import { ref } from 'vue';
import request from '@/utils/request';

import type { R } from '@/api';
import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import type { MessageHandler } from 'element-plus';

import { EleMessage } from 'ele-admin-plus';
import { API_BASE_URL, FILE_API_BASE_URL } from '@/config/setting';
import { getToken } from './token-util';
import LzdUtil, {
  dataFillCurrInfo,
  formDataFillCurrInfo,
  JSONUtil
} from './lzd-util';
import type { FileRecord } from '@/api/bsl/system/file/types';

/** 文档上传配置 */
export const FileUploadPkConfig = Object.freeze({
  /** 文档，加密，需要登录才能下载: .zip, .doc, .docx, .pdf, .wps, .ppt, pptx */
  document: 1,
  /** 图片，不加密，不需要登录:.png, .jpeg, .jpg, .gif, .icon, .svg */
  image: 2,
  /** 图片，不加密，需要登录才能访问: .png, .jpeg, .jpg, .gif, .icon, .svg */
  imageLogin: 3,
  /** 文档模板，不加密，不需要登录: .zip, .doc, .docx, .pdf, .wps, .ppt, pptx */
  documentTpl: 4,
  /** 富文本编辑器图片，不加密，不需要登录: .png, .jpeg, .jpg, .gif, .icon, .svg */
  editorImage: 5,
  /** 头像，不加密，不需要登录: .doc, .docx, .pdf, .wps, .ppt, pptx */
  userAvatar: 6,
  /** 用户简历，加密，需要登录才能下载: .pdf,.xls,.xlsx,.ppt,.pptx,.doc,.docx,.zip,.rar,.7z */
  userResume: 7,
  /** 租户通知公告资料，不加密，需要登录才能下载: .pdf,.xls,.xlsx,.ppt,.pptx,.doc,.docx,.zip,.rar,.7z */
  tenantNotice: 8,
  /** 租户电子资料，不加密，需要登录才能下载: .pdf,.xls,.xlsx,.ppt,.pptx,.doc,.docx,.zip,.rar,.7z */
  tenantResource: 9,
  /** 答辩组图片，不加密，不需要登录: .png,.jpeg,.jpg,.gif */
  defenseImage: 10,
  /** 答辩组资料，不加密，需要登录才能下载: .pdf,.xls,.xlsx,.ppt,.pptx,.doc,.docx,.zip,.rar */
  defenseResource: 11,
  /** 签名图片，不加密，不需要登录: .png,.jpeg,.jpg,.gif */
  signatureImage: 12,
  /** 异步导出数据存储，加密，需要登录 */
  asyncExportData: 13,
  /** 全国学位论文抽检数据存储，加密，需要登录 */
  thesisNdsi: 14,
  /** 应用模板文件 */
  appTpl: 15,
  /** 需要匿名处理的文件 */
  documentAnonymous: 16
});

/** 导入进度 */
const progress = ref(0);

const blacklist = [
  '.exe',
  '.jsp',
  '.js',
  '.ts',
  '.html',
  '.htm',
  '.vue',
  '.css',
  '.java',
  '.php',
  '.cpp',
  '.h'
];

/**
 * 文件上传
 * @param fileConfigSeq 文件上传配置
 * @param file 文件
 * @param config 请求配置
 */
async function uploadFile(
  fileConfigSeq: number,
  file: File,
  config: AxiosRequestConfig = {}
) {
  const i18nLang = localStorage.getItem('i18n-lang') || 'zh_CN'; // zh_CN zh_TW en
  const fileExtension = file.name
    .substring(file.name.lastIndexOf('.'))
    .toLowerCase();
  if (blacklist.includes(fileExtension)) {
    if ('zh_TW' === i18nLang) {
      return Promise.reject(new Error(`不支持 ${fileExtension} 格式上傳`));
    }
    if ('en' === i18nLang) {
      return Promise.reject(
        new Error(`Uploading of ${fileExtension} format is not supported`)
      );
    }
    return Promise.reject(new Error(`不支持 ${fileExtension} 格式上传`));
  }

  // 小于50B 的直接返回
  if (!file || file.size < 50) {
    if ('zh_TW' === i18nLang) {
      return Promise.reject(
        new Error('您上傳的檔案小於50B，上傳失敗，請核實。')
      );
    }
    if ('en' === i18nLang) {
      return Promise.reject(
        new Error(
          'The file you uploaded is less than 50B, upload failed. Please verify.'
        )
      );
    }
    return Promise.reject(new Error('您上传的文件小于50B，上传失败，请核实。'));
  }

  let loading: MessageHandler | null = null;
  if (!config.onUploadProgress) {
    progress.value = 0;

    const fnUploadMessage = () => {
      if ('zh_TW' === i18nLang) {
        return (
          progress.value !== 100
            ? `上傳中...${progress.value}%`
            : '上傳成功，請稍後...'
        ) as any;
      }
      if ('en' === i18nLang) {
        return (
          progress.value !== 100
            ? `Uploading...${progress.value}%`
            : 'Upload success, please wait...'
        ) as any;
      }
      return (
        progress.value !== 100
          ? `上传中，请勿关闭或刷新浏览器...${progress.value}%`
          : '上传成功，请稍后...'
      ) as any;
    };
    loading = EleMessage.loading({
      message: fnUploadMessage,
      plain: true,
      inner: false,
      mask: true,
      centered: true
    });
    config.onUploadProgress = (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.progress! >= 0.86) {
        progress.value = 86;
      } else {
        progress.value = Math.ceil(progressEvent.progress! * 100);
      }
    };
  }
  const formData = new FormData();
  formData.append('file', file);
  formDataFillCurrInfo(formData);
  const res = await request.post<R<FileRecord>>(
    FILE_API_BASE_URL + '/bsl/file/upload/' + fileConfigSeq,
    formData,
    config
  );
  if (res.data.code === 0) {
    const fd = res.data.data;
    // 小于50B 的直接返回
    if (fd && (!fd.fileSize || fd.fileSize < 50)) {
      loading?.close();
      if ('zh_TW' === i18nLang) {
        return Promise.reject(
          new Error('您上傳的檔案小於50B，上傳失敗，請核實。')
        );
      }
      if ('en' === i18nLang) {
        return Promise.reject(
          new Error(
            'The file you uploaded is less than 50B, upload failed. Please verify.'
          )
        );
      }
      return Promise.reject(
        new Error('您上传的文件小于50B，上传失败，请核实。')
      );
    }

    progress.value = 100;
    setTimeout(() => loading?.close(), 350);
    return fd;
  }
  loading?.close();
  return Promise.reject(new Error(res.data.message));
}

/**
 * 文件上传
 * @param fileConfigSeq 文件上传配置
 * @param file 文件
 * @param config 请求配置
 */
async function uploadByUrl(
  fileConfigSeq: number,
  fileUrl: string,
  fileName: string,
  config: AxiosRequestConfig = {}
) {
  const i18nLang = localStorage.getItem('i18n-lang') || 'zh_CN'; // zh_CN zh_TW en
  let loading: MessageHandler | null = null;
  if (!config.onUploadProgress) {
    progress.value = 0;

    const fnUploadMessage = () => {
      if ('zh_TW' === i18nLang) {
        return (
          progress.value !== 100
            ? `上傳中...${progress.value}%`
            : '上傳成功，請稍後...'
        ) as any;
      }
      if ('en' === i18nLang) {
        return (
          progress.value !== 100
            ? `Uploading...${progress.value}%`
            : 'Upload success, please wait...'
        ) as any;
      }
      return (
        progress.value !== 100
          ? `上传中...${progress.value}%`
          : '上传成功，请稍后...'
      ) as any;
    };
    loading = EleMessage.loading({
      message: fnUploadMessage,
      plain: true,
      inner: false,
      mask: true,
      centered: true
    });
    config.onUploadProgress = (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.progress! >= 0.86) {
        progress.value = 86;
      } else {
        progress.value = Math.ceil(progressEvent.progress! * 100);
      }
    };
  }
  const formData = new FormData();
  formData.append('fileUrl', fileUrl);
  formData.append('fileName', fileName);
  formDataFillCurrInfo(formData);
  const res = await request.post<R<FileRecord>>(
    FILE_API_BASE_URL + '/bsl/file/upload/by/url/' + fileConfigSeq,
    formData,
    config
  );
  if (res.data.code === 0) {
    const fd = res.data.data as any;
    // 小50B 的直接返回
    if (fd && (!fd.fileSize || fd.fileSize < 50)) {
      loading?.close();
      if ('zh_TW' === i18nLang) {
        return Promise.reject(
          new Error('您上傳的檔案小於50B，上傳失敗，請核實。')
        );
      }
      if ('en' === i18nLang) {
        return Promise.reject(
          new Error(
            'The file you uploaded is less than 50B, upload failed. Please verify.'
          )
        );
      }
      return Promise.reject(
        new Error('您上传的文件小于50B，上传失败，请核实。')
      );
    }

    progress.value = 100;
    setTimeout(() => loading?.close(), 350);
    return res.data.data;
  }
  loading?.close();
  return Promise.reject(new Error(res.data.message));
}

/** 上传文档 */
async function uploadFileDocument(file: File, config: AxiosRequestConfig = {}) {
  return uploadFile(FileUploadPkConfig.document, file, config);
}

/** 上传文档模板 */
async function uploadFileDocumentTpl(
  file: File,
  config: AxiosRequestConfig = {}
) {
  return uploadFile(FileUploadPkConfig.documentTpl, file, config);
}

/** 上传图片，不登录也能访问 */
async function uploadFileImage(file: File, config: AxiosRequestConfig = {}) {
  return uploadFile(FileUploadPkConfig.image, file, config);
}

/** 上传图片，需登录才能访问 */
async function uploadFileImageByLogin(
  file: File,
  config: AxiosRequestConfig = {}
) {
  return uploadFile(FileUploadPkConfig.imageLogin, file, config);
}

/** 全国学位论文抽检数据存储，加密，需登录 */
export async function uploadFileThesisNdsi(
  file: File,
  config: AxiosRequestConfig = {}
) {
  return uploadFile(FileUploadPkConfig.thesisNdsi, file, config);
}

/** 上传应用 文件 */
export async function uploadFileAppTpl(
  file: File,
  config: AxiosRequestConfig = {}
) {
  if (!/\.(jar|zip)$/i.test(file.name)) {
    return LzdUtil.tipsError('仅支持 .jar 或 .zip 格式');
  }

  return uploadFile(FileUploadPkConfig.appTpl, file, config);
}

/** 上传需要匿名处理的文件 */
export async function uploadFileDocumentAnonymous(
  file: File,
  config: AxiosRequestConfig = {}
) {
  if (!/\.(docx?|pdf)$/i.test(file?.name)) {
    return LzdUtil.tipsError('仅支持 .doc .docx .pdf 格式');
  }

  return uploadFile(FileUploadPkConfig.documentAnonymous, file, config);
}

/** 上传头像 */
async function uploadFileUserAvatar(
  file: File,
  config: AxiosRequestConfig = {}
) {
  return uploadFile(FileUploadPkConfig.userAvatar, file, config);
}

/**
 * 下载文件
 */
async function downloadFile(fileUrl: string, fileName?: string) {
  fileUrl = /^https?:\/\//.test(fileUrl) ? fileUrl : API_BASE_URL + fileUrl;

  // 拼上文件扩展名
  if (fileName) {
    try {
      let fileExtension = fileUrl.split('?', 2)[0];
      fileExtension = fileExtension.substring(fileExtension.lastIndexOf('.'));
      if (
        fileExtension &&
        fileExtension.length > 3 &&
        fileExtension.length < 8 &&
        !fileName.toLowerCase().endsWith(fileExtension.toLowerCase())
      ) {
        fileName += fileExtension;
      }
    } catch (_err) {}
  }

  downloadForm(fileUrl, 'GET', { fileName });
}

/**
 * 下载文件, 使用form标签下载
 * @param fileUrl 下载地址
 * @param data 下载参数
 */
async function downloadForm(
  fileUrl: string,
  method: 'POST' | 'GET',
  data: Record<string, string | number | boolean | undefined> = {},
  file?: File
) {
  const loading = EleMessage.loading('加载中...');
  try {
    fileUrl = /^https?:\/\//.test(fileUrl) ? fileUrl : API_BASE_URL + fileUrl;
    const param: Record<string, any> =
      JSONUtil.parse(data.param as string) ?? {};
    dataFillCurrInfo(param);
    data.param = JSONUtil.toStr(param);

    const form = document.createElement('form');
    form.style.display = 'none';
    form.method = method;
    form.enctype = 'multipart/form-data'; // 确保表单可以上传文件
    form.action = fileUrl;
    // 密钥
    data.access_token = getToken() || '';
    data.download = true;
    Object.keys(data)
      .filter((key) => data[key] !== undefined)
      .forEach((key) => {
        const input = document.createElement('input');
        input.name = key;
        input.type = 'hidden';
        input.value = String(data[key]);
        form.appendChild(input);
      });

    if (file) {
      // 创建文件输入框
      const fileInput = document.createElement('input');
      fileInput.type = 'file'; // 指定 input 类型为文件
      fileInput.name = 'file'; // 设置文件的字段名
      form.appendChild(fileInput); // 将文件 input 添加到表单中

      // 将文件对象放入 FileList
      const dataTransfer = new DataTransfer(); // 创建一个 DataTransfer 对象
      dataTransfer.items.add(file); // 将文件添加到 DataTransfer 中
      fileInput.files = dataTransfer.files; // 将 FileList 设置为 input 的 files 属性
    }

    document.body.appendChild(form);

    form.submit();

    // 3S后删除
    setTimeout(() => form.remove(), 1000 * 15);
    // }
  } catch (error) {
    console.error('Error:', error);
  } finally {
    setTimeout(() => loading.close(), 3000);
  }
}

/**
 * 下载文件, 使用form标签下载
 * @param fileUrl 下载地址
 * @param data 下载参数
 */
async function downloadPostFile(
  fileUrl: string,
  data: Record<string, string | number | boolean | undefined> = {},
  file?: File
) {
  downloadForm(fileUrl, 'POST', data, file);
}

/**
 * 先判断文件是否存在，存在再执行下载操作
 */
async function downloadPreflightFile(fileUrl: string, fileName?: string) {
  const res = await request.get<R<unknown>>(fileUrl, {
    params: { expired: true }
  });
  if (res.status === 404) {
    return Promise.reject(new Error('Not Found'));
  }

  downloadFile(fileUrl, fileName);
}

/**
 * 图片分割(删除背景), 返回base64字符，使用时需加上 data:image/png;base64,
 */
async function imageSegmentation(file: File) {
  const fileExtension = file.name
    .substring(file.name.lastIndexOf('.'))
    .toLowerCase();
  if (blacklist.includes(fileExtension)) {
    return Promise.reject(new Error(`不支持 '${fileExtension}' 格式上传`));
  }
  const formData = new FormData();
  formData.append('file', file);
  const res = await request.post<R<string>>(
    FILE_API_BASE_URL + '/bsl/file/image/segmentation',
    formData
  );
  if (res.data.code === 0) {
    return res.data.data;
  }
  return Promise.reject(new Error(res.data.message));
}

/**
 * Base64字符 转 File
 */
function base64ToFile(base64Str: string) {
  // 提取Base64编码的数据部分（去掉前缀）
  base64Str = base64Str.replace(/^data:image\/(png|jpeg);base64,/, '');

  // 将Base64编码的数据解码为二进制字符串
  const binaryString = globalThis.atob(base64Str);

  // 将二进制字符串转换为Uint8Array
  const arrayBuffer = new ArrayBuffer(binaryString.length);
  const intArray = new Uint8Array(arrayBuffer);
  for (let i = 0; i < binaryString.length; i++) {
    intArray[i] = binaryString.charCodeAt(i);
  }

  // 创建一个Blob对象
  const blob = new Blob([intArray], { type: 'image/png' });

  // 创建一个File对象， 并指定文件名
  const file = new File([blob], 'base64-to-file.png');

  return file;
}

/** 文件上传 API */
class fileUtil {
  static uploadFile = uploadFile;
  static uploadByUrl = uploadByUrl;
  static uploadFileDocument = uploadFileDocument;
  static uploadFileDocumentTpl = uploadFileDocumentTpl;
  static uploadFileUserAvatar = uploadFileUserAvatar;
  static uploadFileImage = uploadFileImage;
  static uploadFileImageByLogin = uploadFileImageByLogin;
  static uploadFileThesisNdsi = uploadFileThesisNdsi;
  static uploadFileAppTpl = uploadFileAppTpl;
  static uploadFileDocumentAnonymous = uploadFileDocumentAnonymous;
  static downloadFile = downloadFile;
  static downloadPostFile = downloadPostFile;
  static downloadPreflightFile = downloadPreflightFile;
  static imageSegmentation = imageSegmentation;
  static base64ToFile = base64ToFile;
}

export default fileUtil;
